import { useState, useEffect } from "react";
import { fetchAuditLogsApi } from "../Lolbin/Api/lolbinapi";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
    Settings,
    ArrowLeft,
    CheckCircle,
    X,
    AlertTriangle,
    Key,
    BarChart3,
    Activity,
    Shield,
    Bell,
    Server,
    Globe,
    Monitor,
    AlertCircle,
    Zap,
    TrendingUp,
    User
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import AuditLogsDetails from "../AuditLogs/AuditLogsDetails";
import AlertDetails from "../Alerts/AlertsDetails";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import LolbinDetails from "../Lolbin/LolbinDetails";

interface IPAddressUsage {
    ipAddress: string;
    accessCount: number;
    lastSeen: string;
}


interface SPNOperation {
    spn: string;
    accessCount: number;
    lastUsed: string;
}


const isInternalIP = (ip: string): boolean => {
    const parts = ip.split('.').map(Number);
    if (parts.length !== 4) return false;
    if (parts[0] === 10) return true;
    if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
    if (parts[0] === 192 && parts[1] === 168) return true;
    if (parts[0] === 127) return true;
    return false;
};


export default function ADUserDetailsAnalytics() {
    const [lastActivity, setLastActivity] = useState<string>("-");
    const params = useParams();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState("alerts");
    const navigate = useNavigate();
    const computerEmail = params.name || '';
    const samAccountName = params.samAccountName || '';

    const [topIPAddresses, setTopIPAddresses] = useState<IPAddressUsage[]>([]);
    const [spnOperations, setSpnOperations] = useState<SPNOperation[]>([]);


    return (
        <div className="flex-1 space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center gap-4" onClick={() => navigate(-1)}>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="button-back">
                    <ArrowLeft className="w-4 h-4" />
                    Back to AD Management
                </Button>
            </div>

            {/* Entity Header Card - Compact with Stats */}
            <Card>
                <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center border bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 border-blue-200 dark:border-blue-700">
                                <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-3 mb-1">
                                    <h1 className="text-xl font-bold tracking-tight" data-testid="text-entity-name">
                                        {computerEmail}
                                    </h1>
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Active
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-6 text-xs text-muted-foreground">
                                    {/*<span>Managed by <span className="font-medium text-foreground">{entityData.managedBy}</span></span>*/}
                                    {/* No lastLogin available from API, so omit or use a placeholder */}
                                    <span className="font-medium text-foreground">{samAccountName}</span>
                                </div>
                            </div>
                        </div>
                        {/* Quick Stats on right 
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-blue-600" />
                                <div>
                                    <div className="text-lg font-bold">{topIPAddresses.length}</div>
                                    <div className="text-[10px] text-muted-foreground">IP Addresses</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Key className="w-4 h-4 text-purple-600" />
                                <div>
                                    <div className="text-lg font-bold">{spnOperations.length}</div>
                                    <div className="text-[10px] text-muted-foreground">SPNs</div>
                                </div>
                            </div>
                        </div>
                        */}
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Card className="flex-1">
                <CardContent className="pt-6">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-3">

                            <TabsTrigger value="alerts" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                                <Bell className="w-4 h-4" />
                                Alerts
                            </TabsTrigger>
                            <TabsTrigger value="lolbin" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                                <BarChart3 className="w-4 h-4" />
                                Lolbin
                            </TabsTrigger>
                            <TabsTrigger value="activity" className="gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                                <Activity className="w-4 h-4" />
                                Activity
                            </TabsTrigger>


                        </TabsList>


                        {/* Lolbin Tab - Dynamic Data */}
                        <TabsContent value="lolbin" className="space-y-6 mt-6">
                            <LolbinDetails
                                fromdetails={true}
                                computerName=""
                                computeremail={samAccountName}
                                showTopLolBins={true}
                                showTopUsers={false}
                                showTopComputers={true}
                            />
                        </TabsContent>

                        {/* Activity Tab */}
                        <TabsContent value="activity" className="space-y-4 mt-6">
                            <AuditLogsDetails fromdetails={true} computerName="" computeremail={samAccountName} />
                        </TabsContent>

                        {/* Alerts Tab */}
                        <TabsContent value="alerts" className="space-y-4 mt-6">
                            <Card>
                                <CardContent>
                                    <AlertDetails fromdetails={true} computerName="" computeremail={samAccountName} />
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
}
